import _ from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'store';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import {
  HookQuerySearchSelect,
  isFreeSoloOption,
  HookFormToggle,
  HookFormInput,
  HookFormToggleCardButton,
  HookFormTelephoneInput,
  HookFormTableInput,
  HookFormSelect,
} from 'ui-component/HookFormComponents';
import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import {
  formConstants,
  getCopy,
  updateValidationSchema,
  createValidationSchema,
} from 'views/suppliers/CreateEditOrgSupplierDialog/constants';
import { handleErr } from 'utils/functions';
import { isCofactrUser } from 'utils/userPermissions';
import {
  CreateOrgSupplier,
  OrgSupplier,
  OrgSupplierConnection,
  OrgSupplierConnectionLabels,
  PublicSupplier,
} from 'types/suppliers';
import {
  useCreateOrgSupplierMutation,
  useGetOrgSuppliersServerSideQuery,
  useGetPublicSuppliersServerSideQuery,
  useUpdateOrgSupplierMutation,
} from 'store/slices/apiV1/suppliers';
import useSnackbar from 'hooks/useSnackbar';
import { IconRoute2, IconTopologyStar } from '@tabler/icons-react';
import { Alert, AlertTitle, Box, MenuItem, Stack } from '@mui/material';
import {
  ExtendedStep,
  useStepper,
  VerticalStepperContainer,
} from 'ui-component/VerticalStepper';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { ALL_APP_IDS, ALL_FEATURE_IDS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { RecordTypes } from 'ui-component/DetailCustomFields';
import useGetCustomFieldDefinitions from 'hooks/useGetCustomFieldDefinitions';
import { useGetOrgQuery } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useDocumentTemplates } from 'hooks/useDocumentTemplates';
import { StockDocType } from 'types/inventory';
import useGetCustomPropertyDefinitions from 'hooks/useGetCustomPropertyDefinitions';
import {
  CustomPropertyModel,
  CustomPropertyVisibility,
} from 'types/customProperty';
import { HookFormCustomPropertyInput } from 'ui-component/HookFormComponents/HookFormCustomPropertyInput';
import { HookFormQuillInput } from 'ui-component/HookFormComponents/HookFormQuillInput';

const CreateEditOrgSupplierDialog = ({
  dialogOpen,
  onClose,
  publicSupplier: initialPublicSupplier,
  selectedSupplier,
  setSelectedSupplier,
}: {
  dialogOpen: boolean;
  onClose: () => void;
  publicSupplier?: PublicSupplier | null;
  selectedSupplier?: OrgSupplier | null;
  setSelectedSupplier?: (supplier: OrgSupplier) => void;
}) => {
  const { dispatchSuccessSnackbar } = useSnackbar();
  const { data: user } = useGetUserQuery(undefined);
  const { viewAsStaff } = useSelector((state) => state.user);
  const isCofactr = isCofactrUser({ user, viewAsStaff });
  const copy = getCopy(!!selectedSupplier);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { hasFeaturePermission } = useAppAccessContext();
  const { activeOrgId } = useSelector((state) => state.org);
  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);

  const [createSupplier, { isLoading: isCreatingSupplier }] =
    useCreateOrgSupplierMutation();

  const [updateSupplier, { isLoading: isUpdatingSupplier }] =
    useUpdateOrgSupplierMutation();

  const { groupedDocumentTemplates, defaultDocumentTemplates } =
    useDocumentTemplates();

  const initialFormValues = selectedSupplier
    ? {
        ...selectedSupplier,
        [formConstants.freeShipThreshold.id]:
          selectedSupplier.freeShip?.threshold,
        [formConstants.freeShipShippingLead.id]:
          selectedSupplier.freeShip?.shippingLead,
      }
    : initialPublicSupplier
    ? {
        [formConstants.supplier.id]: initialPublicSupplier,
        [formConstants.alsoCreatePublicSupplier.id]: false,
        [formConstants.name.id]: initialPublicSupplier.name,
      }
    : {
        [formConstants.supplier.id]: '',
        [formConstants.alsoCreatePublicSupplier.id]: false,
        [formConstants.name.id]: '',
        [formConstants.connectionType.id]: '',
        [formConstants.orderEmail.id]: '',
        [formConstants.poFooterNotes.id]: org?.poFooterNotes,
        [formConstants.defaultPoTemplate.id]: _.get(
          defaultDocumentTemplates,
          [StockDocType.PURCHASE_ORDER, 'id'],
          null
        ),
      };

  const methods = useForm<FieldValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(
      selectedSupplier ? updateValidationSchema : createValidationSchema
    ),
    mode: 'all',
  });

  const {
    control,
    reset,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    watch,
    setValue,
    resetField,
    trigger,
  } = methods;

  const { customFieldDefinitions } = useGetCustomFieldDefinitions({
    recordType: RecordTypes.orgSupplier,
  });

  const { customProperties } = useGetCustomPropertyDefinitions({
    model: CustomPropertyModel.ORG_SUPPLIER,
    visibilityContext: selectedSupplier
      ? CustomPropertyVisibility.EDIT_RECORD_DIALOG
      : CustomPropertyVisibility.NEW_RECORD_DIALOG,
  });

  const watchPublicSupplier = watch(formConstants.supplier.id);
  const publicSupplier =
    selectedSupplier?.publicSupplier ||
    initialPublicSupplier ||
    watchPublicSupplier;
  const connectionType = watch(formConstants.connectionType.id);
  const name = watch(formConstants.name.id);
  const orderEmail = watch(formConstants.orderEmail.id);
  const useOrderSubmissionApi = watch(formConstants.useOrderSubmissionApi.id);

  const { data: { data: duplicateSuppliers = [] } = { data: [] } } =
    useGetOrgSuppliersServerSideQuery(
      (name || watchPublicSupplier) && !selectedSupplier
        ? {
            schema: ['id', 'name'],
            filters: [
              {
                field: 'name',
                operator: 'equals',
                value: name || watchPublicSupplier?.name,
              },
            ],
            pageSize: 1,
          }
        : skipToken
    );

  useEffect(() => {
    if (orderEmail) {
      setValue(formConstants.quotingEmail.id, orderEmail);
    }
    if (orderEmail) {
      setValue(formConstants.paymentEmail.id, orderEmail);
    }
  }, [orderEmail]);

  useEffect(() => {
    if (
      !useOrderSubmissionApi &&
      !!connectionType &&
      connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK
    ) {
      setValue(formConstants.allowNonPartPurchaseLines.id, true);
    } else if (useOrderSubmissionApi) {
      setValue(formConstants.allowNonPartPurchaseLines.id, false);
    }
    if (
      !!connectionType &&
      connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK
    ) {
      setValue(formConstants.allowDropship.id, true);
    }
  }, [useOrderSubmissionApi, connectionType]);

  useEffect(() => {
    reset(initialFormValues);
  }, [selectedSupplier, initialPublicSupplier]);

  useEffect(() => {
    if (
      (!!publicSupplier && isFreeSoloOption(publicSupplier)) ||
      !org?.allowCofactrManagedPurchasing
    ) {
      setValue(formConstants.connectionType.id, OrgSupplierConnection.DIRECT);
    } else if (publicSupplier) {
      setValue(formConstants.website.id, publicSupplier.website);
      setValue(formConstants.contactName.id, publicSupplier.contactName);
      setValue(formConstants.phone.id, publicSupplier.phone);
      setValue(formConstants.orderEmail.id, publicSupplier.orderEmail);
      setValue(formConstants.quotingEmail.id, publicSupplier.quotingEmail);
      setValue(formConstants.paymentEmail.id, publicSupplier.paymentEmail);
      setValue(
        formConstants.customReelPrice.id,
        publicSupplier.customReelPrice
      );
      setValue(
        formConstants.minimumLineSize.id,
        publicSupplier.minimumLineSize
      );
      setValue(
        formConstants.minimumOrderSize.id,
        publicSupplier.minimumOrderSize
      );
      setValue(
        formConstants.minimumOrderForScheduledRelease.id,
        publicSupplier.minimumOrderForScheduledRelease
      );
      setValue(
        formConstants.minimumLineForScheduledRelease.id,
        publicSupplier.minimumLineForScheduledRelease
      );
      setValue(
        formConstants.shippingOptions.id,
        publicSupplier.shippingOptions
      );
      setValue(
        formConstants.freeShipShippingLead.id,
        publicSupplier.freeShip?.shippingLead
      );
      setValue(
        formConstants.freeShipThreshold.id,
        publicSupplier.freeShip?.threshold
      );
      setValue(
        formConstants.shippingPriceIsEstimate.id,
        publicSupplier.shippingPriceIsEstimate
      );
      setValue(formConstants.bestCaseLead.id, publicSupplier.bestCaseLead);
      setValue(
        formConstants.scheduledReleasePeriod.id,
        publicSupplier.scheduledReleasePeriod
      );
      setValue(
        formConstants.convenienceReturnWindow.id,
        publicSupplier.convenienceReturnWindow
      );
      setValue(formConstants.uncertainLead.id, publicSupplier.uncertainLead);
      setValue(formConstants.shipsInLead.id, publicSupplier.shipsInLead);
      setValue(formConstants.allowDropship.id, publicSupplier.allowDropship);
    }
  }, [publicSupplier]);

  const steps = [
    {
      label: 'Basic Info',
      preNext: async () =>
        trigger([
          formConstants.supplier.id,
          formConstants.name.id,
          formConstants.connectionType.id,
          formConstants.usePricingApi.id,
          formConstants.useOrderSubmissionApi.id,
          formConstants.useOrderStatusApi.id,
          formConstants.useOrderPo.id,
          formConstants.useOnlineCheckout.id,
          formConstants.useCustomReeling.id,
          formConstants.allowNonPartPurchaseLines.id,
          formConstants.alsoCreatePublicSupplier.id,
        ]),
      component: (
        <>
          {!selectedSupplier ? (
            <HookQuerySearchSelect<PublicSupplier>
              label={formConstants.supplier.label}
              name={formConstants.supplier.id}
              useGetQuery={useGetPublicSuppliersServerSideQuery}
              errors={errors}
              control={control}
              searchSchema={['name']}
              schema={['__all__']}
              getOptionLabel={(option: PublicSupplier) => option?.name}
              overrideOnChange={(value) => {
                const supplier = value as PublicSupplier;
                return supplier;
              }}
              getDefaultValue={(option: Pick<PublicSupplier, 'name' | 'id'>) =>
                option.id
              }
              freeSolo
              matchBy="name"
              disableClearable
            />
          ) : (
            <HookFormInput
              control={control}
              errors={errors}
              label={formConstants.name.label}
              name={formConstants.name.id}
            />
          )}
          {duplicateSuppliers.length > 0 && watchPublicSupplier?.name && (
            <Alert severity="error">
              <AlertTitle>Duplicate Supplier</AlertTitle>
              There is already a Supplier record for{' '}
              {publicSupplier?.name || name}. To edit settings{' '}
              {publicSupplier?.name || name}, search for{' '}
              {publicSupplier?.name || name} in the Supplier view.
            </Alert>
          )}
          {((!!publicSupplier && !isFreeSoloOption(publicSupplier)) ||
            (!!selectedSupplier && !!selectedSupplier.publicSupplier)) &&
            org?.allowSelfManagedPurchasing &&
            org?.allowCofactrManagedPurchasing && (
              <HookFormToggleCardButton
                key={formConstants.connectionType.id}
                name={formConstants.connectionType.id}
                control={control}
                errors={errors}
                label={formConstants.connectionType.label}
                options={[
                  {
                    value: OrgSupplierConnection.SUPPLIER_NETWORK,
                    label:
                      OrgSupplierConnectionLabels[
                        OrgSupplierConnection.SUPPLIER_NETWORK
                      ],
                    description: `Automatically set up the connection between Cofactr & ${
                      publicSupplier?.name || name
                    }. We will fully manage your ${
                      publicSupplier?.name || name
                    } purchases for you.`,
                    icon: IconTopologyStar,
                  },
                  {
                    value: OrgSupplierConnection.DIRECT,
                    label:
                      OrgSupplierConnectionLabels[OrgSupplierConnection.DIRECT],
                    description: `Configure your own connection to ${
                      publicSupplier?.name || name
                    }.`,
                    icon: IconRoute2,
                  },
                ]}
              />
            )}
          {!!connectionType &&
            connectionType === OrgSupplierConnection.DIRECT &&
            !org?.allowSelfManagedPurchasing && (
              <Alert severity="error">
                <AlertTitle>Direct Connection Not Allowed</AlertTitle>
                You have selected to connect to {publicSupplier?.name ||
                  name}{' '}
                directly but your organization preferences only allow purchasing
                using Managed by Cofactr.
              </Alert>
            )}
          {!!connectionType &&
            connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK &&
            publicSupplier &&
            publicSupplier.supportsPricingApi && (
              <HookFormToggle
                control={control}
                errors={errors}
                label={formConstants.usePricingApi.label}
                name={formConstants.usePricingApi.id}
                helperText={
                  hasFeaturePermission(ALL_FEATURE_IDS.SUPPLIER_PRICING_API)
                    ? formConstants.usePricingApi.description
                    : 'Upgrade your Cofactr subscription to use custom supplier API connections'
                }
                disabled={
                  !hasFeaturePermission(ALL_FEATURE_IDS.SUPPLIER_PRICING_API)
                }
              />
            )}
          {!!connectionType &&
            connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK &&
            publicSupplier &&
            publicSupplier.supportsOrderSubmissionApi && (
              <HookFormToggle
                control={control}
                errors={errors}
                label={formConstants.useOrderSubmissionApi.label}
                name={formConstants.useOrderSubmissionApi.id}
                helperText={
                  hasFeaturePermission(ALL_FEATURE_IDS.SUPPLIER_ORDER_API)
                    ? formConstants.useOrderSubmissionApi.description
                    : 'Upgrade your Cofactr subscription to use custom supplier API connections'
                }
                disabled={
                  !hasFeaturePermission(ALL_FEATURE_IDS.SUPPLIER_ORDER_API)
                }
              />
            )}
          {useOrderSubmissionApi &&
            !!connectionType &&
            connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK &&
            publicSupplier &&
            publicSupplier.supportsOrderStatusApi && (
              <HookFormToggle
                control={control}
                errors={errors}
                label={formConstants.useOrderStatusApi.label}
                name={formConstants.useOrderStatusApi.id}
                helperText={
                  hasFeaturePermission(ALL_FEATURE_IDS.SUPPLIER_ORDER_API)
                    ? formConstants.useOrderStatusApi.description
                    : 'Upgrade your Cofactr subscription to use custom supplier API connections'
                }
                disabled={
                  !hasFeaturePermission(ALL_FEATURE_IDS.SUPPLIER_ORDER_API)
                }
              />
            )}
          {(watch(formConstants.usePricingApi.id) ||
            useOrderSubmissionApi ||
            watch(formConstants.useOrderStatusApi.id)) && (
            <Alert severity="warning">
              <AlertTitle>Contact Cofactr Success</AlertTitle>
              Once you complete the process of adding{' '}
              {publicSupplier?.name || name}, please contact Cofactr Success, so
              we can finish the API connection setup process for you. It will
              not be possible to use {publicSupplier?.name || name} APIs until
              the connection is configured.
            </Alert>
          )}
          {!useOrderSubmissionApi &&
            !!connectionType &&
            connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK && (
              <HookFormToggle
                control={control}
                errors={errors}
                label={formConstants.useOrderPo.label}
                name={formConstants.useOrderPo.id}
                helperText={
                  hasFeaturePermission(ALL_FEATURE_IDS.GENERATE_PO)
                    ? formConstants.useOrderPo.description
                    : 'Upgrade your Cofactr subscription to send Purchase Orders'
                }
                disabled={!hasFeaturePermission(ALL_FEATURE_IDS.GENERATE_PO)}
              />
            )}
          {!useOrderSubmissionApi &&
            !!connectionType &&
            connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK && (
              <HookFormToggle
                control={control}
                errors={errors}
                label={formConstants.useOnlineCheckout.label}
                name={formConstants.useOnlineCheckout.id}
                helperText={
                  hasFeaturePermission(ALL_FEATURE_IDS.SUPPLIER_ONLINE_CHECKOUT)
                    ? formConstants.useOnlineCheckout.description
                    : 'Upgrade your Cofactr subscription to use online checkout workflows'
                }
                disabled={
                  !hasFeaturePermission(
                    ALL_FEATURE_IDS.SUPPLIER_ONLINE_CHECKOUT
                  )
                }
              />
            )}
          {!useOrderSubmissionApi &&
            !!connectionType &&
            (connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK ||
              (publicSupplier &&
                publicSupplier.supportsNonPartPurchaseLines)) && (
              <HookFormToggle
                control={control}
                errors={errors}
                label={formConstants.allowNonPartPurchaseLines.label}
                name={formConstants.allowNonPartPurchaseLines.id}
                helperText={
                  hasFeaturePermission(ALL_FEATURE_IDS.NON_PART_PURCHASE_LINES)
                    ? formConstants.allowNonPartPurchaseLines.description
                    : 'Upgrade your Cofactr subscription to use non-part lines in Purchase Orders'
                }
                disabled={
                  !hasFeaturePermission(ALL_FEATURE_IDS.NON_PART_PURCHASE_LINES)
                }
              />
            )}
          {!selectedSupplier &&
            isCofactr &&
            isFreeSoloOption(publicSupplier) && (
              <HookFormToggle
                control={control}
                errors={errors}
                label={formConstants.alsoCreatePublicSupplier.label}
                name={formConstants.alsoCreatePublicSupplier.id}
                helperText={formConstants.alsoCreatePublicSupplier.description}
              />
            )}
        </>
      ),
    },
    ...(!!connectionType &&
    connectionType !== OrgSupplierConnection.SUPPLIER_NETWORK
      ? [
          {
            label: 'Contact',
            preNext: async () =>
              trigger([
                formConstants.contactName.id,
                formConstants.website.id,
                formConstants.phone.id,
                formConstants.orderEmail.id,
                formConstants.quotingEmail.id,
                formConstants.paymentEmail.id,
                formConstants.poFooterNotes.id,
                formConstants.defaultPoTemplate.id,
              ]),
            component: (
              <>
                <HookFormInput
                  control={control}
                  errors={errors}
                  label={formConstants.contactName.label}
                  name={formConstants.contactName.id}
                />
                <HookFormInput
                  control={control}
                  errors={errors}
                  label={formConstants.website.label}
                  name={formConstants.website.id}
                />
                <HookFormTelephoneInput
                  name={formConstants.phone.id}
                  label={formConstants.phone.label}
                  errors={errors}
                  control={control}
                />
                <HookFormInput
                  control={control}
                  errors={errors}
                  label={formConstants.orderEmail.label}
                  name={formConstants.orderEmail.id}
                  helperText={formConstants.orderEmail.description}
                />
                <HookFormInput
                  control={control}
                  errors={errors}
                  label={formConstants.quotingEmail.label}
                  name={formConstants.quotingEmail.id}
                  helperText={formConstants.quotingEmail.description}
                />
                <HookFormInput
                  control={control}
                  errors={errors}
                  label={formConstants.paymentEmail.label}
                  name={formConstants.paymentEmail.id}
                  helperText={formConstants.paymentEmail.description}
                />
                <HookFormQuillInput
                  control={control}
                  errors={errors}
                  label={formConstants.poFooterNotes.label}
                  name={formConstants.poFooterNotes.id}
                  helperText={formConstants.poFooterNotes.description}
                />
                <HookFormSelect
                  control={control}
                  errors={errors}
                  label={formConstants.defaultPoTemplate.label}
                  name={formConstants.defaultPoTemplate.id}
                  mt={0}
                >
                  {_.get(
                    groupedDocumentTemplates,
                    StockDocType.PURCHASE_ORDER,
                    []
                  ).map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.externalName || option.name}
                    </MenuItem>
                  ))}
                </HookFormSelect>
              </>
            ),
          },
          {
            label: 'Shipping',
            preNext: async () =>
              trigger([
                formConstants.shipsInLead.id,
                formConstants.shippingOptions.id,
                formConstants.freeShipThreshold.id,
                formConstants.freeShipShippingLead.id,
              ]),
            component: (
              <>
                <HookFormInput
                  control={control}
                  errors={errors}
                  label={formConstants.shipsInLead.label}
                  name={formConstants.shipsInLead.id}
                  helperText={formConstants.shipsInLead.description}
                  type="number"
                  inputProps={{
                    inputMode: 'numeric',
                  }}
                />
                <HookFormTableInput
                  name={formConstants.shippingOptions.id}
                  control={control}
                  errors={errors}
                  componentProps={{
                    tableProps: { sx: { width: '100%' } },
                    tableCellProps: { sx: { p: 0.5 } },
                  }}
                  fields={[
                    {
                      component: HookFormInput,
                      name: 'shippingLead',
                      label: 'Ship Speed',
                      props: {
                        boxSx: { mb: 0 },
                        type: 'number',
                        inputProps: {
                          inputMode: 'numeric',
                          min: 0,
                        },
                        InputProps: {
                          endAdornment: 'business days',
                        },
                      },
                    },
                    {
                      component: HookFormInput,
                      name: 'price',
                      label: 'Price',
                      props: {
                        boxSx: { mb: 0 },
                        InputProps: {
                          startAdornment: '$',
                        },
                      },
                    },
                  ]}
                />
                <HookFormInput
                  control={control}
                  errors={errors}
                  label={formConstants.freeShipThreshold.label}
                  name={formConstants.freeShipThreshold.id}
                  type="number"
                  boxSx={{ mt: 2 }}
                  inputProps={{
                    inputMode: 'numeric',
                  }}
                />
                {watch(formConstants.freeShipThreshold.id) > 0 && (
                  <HookFormInput
                    control={control}
                    errors={errors}
                    label={formConstants.freeShipShippingLead.label}
                    name={formConstants.freeShipShippingLead.id}
                    type="number"
                    inputProps={{
                      inputMode: 'numeric',
                    }}
                  />
                )}
              </>
            ),
          },
        ]
      : []),
    ...(customFieldDefinitions.length > 0 || customProperties.length > 0
      ? [
          {
            label: 'Other Properties',
            component: (
              <Box sx={{ mt: 2 }}>
                {_.map(customFieldDefinitions, (field) => (
                  <HookFormInput
                    control={methods.control}
                    errors={methods.formState.errors}
                    label={field.fieldLabel}
                    key={field.fieldName}
                    name={`externalMetadata.customFields.${field.fieldName}`}
                  />
                ))}
                {_.map(customProperties, (property) => (
                  <HookFormCustomPropertyInput
                    control={methods.control}
                    errors={methods.formState.errors}
                    key={property.key}
                    customProperty={property}
                    setValue={methods.setValue}
                  />
                ))}
              </Box>
            ),
          },
        ]
      : []),
  ];

  const { activeStep, handleNext, handleBack, resetStep } = useStepper(steps);

  const handleClose = () => {
    resetStep();
    setSubmitError(null);
    onClose();
    reset();
  };

  const onSubmit = async (data: FieldValues) => {
    setSubmitError(null);
    try {
      const payload = {
        ..._.omitBy(
          _.omit(data, [
            formConstants.supplier.id,
            formConstants.freeShipShippingLead.id,
            formConstants.freeShipThreshold.id,
            'publicSupplier',
          ]),
          (val) => _.isNil(val) || val === ''
        ),
        freeShip: data[formConstants.freeShipThreshold.id]
          ? {
              threshold: data[formConstants.freeShipThreshold.id],
              shippingLead: data[formConstants.freeShipShippingLead.id],
            }
          : undefined,
      } as CreateOrgSupplier;

      if (selectedSupplier?.id) {
        // update existing OrgSupplier
        await updateSupplier({
          orgSupplierId: selectedSupplier?.id,
          payload,
        })
          .unwrap()
          .then((fulfilled) => {
            if (setSelectedSupplier) setSelectedSupplier(fulfilled);
          });
      } else if (!isFreeSoloOption(data[formConstants.supplier.id])) {
        // create new OrgSupplier from existing PublicSupplier
        await createSupplier({
          ...payload,
          publicSupplier: data[formConstants.supplier.id]?.id,
        })
          .unwrap()
          .then((fulfilled) => {
            if (setSelectedSupplier) setSelectedSupplier(fulfilled);
          });
      } else {
        // create new OrgSupplier from scratch
        await createSupplier({
          ...payload,
          name: data[formConstants.supplier.id]?.name,
          connectionType: OrgSupplierConnection.DIRECT,
        })
          .unwrap()
          .then((fulfilled) => {
            if (setSelectedSupplier) setSelectedSupplier(fulfilled);
          });
      }
      dispatchSuccessSnackbar(copy.success);

      handleClose();
    } catch (err) {
      handleErr(err, (errMessage: string) => {
        setSubmitError(errMessage);
      });
    }
  };

  return (
    <ExtendedDialog
      open={dialogOpen}
      maxWidth="lg"
      title={copy.title}
      onCloseDialog={handleClose}
      formSubmitError={submitError}
      submitButtonCopy={copy.submit}
      confirmClose={isDirty}
      confirmCloseBodyText="Are you sure you want to close this dialog? You will lose all progress."
      submitDisabled
      isSubmitting={isCreatingSupplier || isUpdatingSupplier}
      overrideComponents={{
        actions: (
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="flex-end"
            width="100%"
          >
            {activeStep > 0 && (
              <SegmentButton
                onClick={() => {
                  handleBack();
                  Object.keys(errors).forEach((field) => resetField(field));
                }}
                sx={{ mt: 1, mr: 1 }}
              >
                Back
              </SegmentButton>
            )}
            {activeStep < steps.length - 1 && (
              <SegmentButton
                variant="contained"
                onClick={() => handleNext()}
                disabled={!selectedSupplier && !isDirty}
                sx={{ mt: 1, mr: 1 }}
              >
                Next
              </SegmentButton>
            )}
            {activeStep === steps.length - 1 && (
              <SegmentLoadingButton
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                disabled={!isDirty}
                loading={isCreatingSupplier || isUpdatingSupplier}
                permissionScope={{
                  app: ALL_APP_IDS.SUPPLIERS,
                  accessLevel: appPermissionAccessLevels.edit,
                }}
              >
                Save
              </SegmentLoadingButton>
            )}
          </Stack>
        ),
      }}
    >
      <FormProvider {...methods}>
        <VerticalStepperContainer stepperProps={{ activeStep }}>
          {steps.map((step, index) => (
            <ExtendedStep key={`${step.label}${index}`} label={step.label}>
              {step.component}
            </ExtendedStep>
          ))}
        </VerticalStepperContainer>
      </FormProvider>
    </ExtendedDialog>
  );
};

export default CreateEditOrgSupplierDialog;
